/*
 * Purpose: Mission integration discovery — proposals scoped to Mission; library on approve
 * Last modified: 2026-06-28 | Agent
 * Completeness: 95
 */
import { create } from 'zustand';
import { toast } from 'sonner';
import type { IntegrationDiscoveryProposal } from '@/services/integrations/types';
import { discoverIntegrationsForMission } from '@/services/integrations/discoveryApi';
import { sortProposalsFreeFirst } from '@/lib/integrationPricing';
import { recordMissionDossier } from '@/services/mission-live/recordLiveAction';
import { retainMissionGeneratedAsync } from '@/services/mission-knowledge/persistGenerated';
import { listConnectorCatalogForPrompt, useConnectorLibraryStore } from '@/services/connectors/library/store';
import type { AutoBlocksConnector } from '@/services/connectors/library/types';

const STORAGE_PREFIX = 'autoblocks:integration-proposals:';

function loadProposals(missionSessionId: string): IntegrationDiscoveryProposal[] {
  if (typeof localStorage === 'undefined' || !missionSessionId) return [];
  try {
    const raw = localStorage.getItem(`${STORAGE_PREFIX}${missionSessionId}`);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as IntegrationDiscoveryProposal[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function persistProposals(missionSessionId: string, proposals: IntegrationDiscoveryProposal[]) {
  if (typeof localStorage === 'undefined' || !missionSessionId) return;
  localStorage.setItem(`${STORAGE_PREFIX}${missionSessionId}`, JSON.stringify(proposals));
}

interface IntegrationDiscoveryStore {
  missionSessionId: string;
  proposals: IntegrationDiscoveryProposal[];
  isDiscovering: boolean;
  lastSearchProvider: string | null;
  error: string | null;
  setMissionSession: (id: string) => void;
  discoverForMission: (input: {
    missionSessionId: string;
    outcome: string;
    interpretedGoal: string;
    gaps?: string[];
  }) => Promise<void>;
  approveProposal: (id: string) => void;
  dismissProposal: (id: string) => void;
  ingestDirectorProposals: (
    missionSessionId: string,
    proposals: IntegrationDiscoveryProposal[]
  ) => void;
}

export const useIntegrationDiscoveryStore = create<IntegrationDiscoveryStore>((set, get) => ({
  missionSessionId: '',
  proposals: [],
  isDiscovering: false,
  lastSearchProvider: null,
  error: null,

  setMissionSession: (id) => {
    set({
      missionSessionId: id,
      proposals: loadProposals(id),
      error: null,
      lastSearchProvider: null,
    });
  },

  discoverForMission: async ({ missionSessionId, outcome, interpretedGoal, gaps }) => {
    if (!missionSessionId || !outcome.trim()) return;
    set({ isDiscovering: true, error: null, missionSessionId });

    const result = await discoverIntegrationsForMission({
      missionSessionId,
      outcome,
      interpretedGoal,
      gaps,
      existingCatalog: listConnectorCatalogForPrompt(),
    });

    if (!result.ok) {
      recordMissionDossier({
        actor: 'system',
        kind: 'error',
        summary: 'Integration discovery failed',
        action: result.message ?? 'Discovery failed',
      });
      set({ isDiscovering: false, error: result.message ?? 'Discovery failed' });
      if (result.message) {
        toast.error(result.message, {
          action: result.action?.href
            ? { label: result.action.label, onClick: () => { window.location.href = result.action!.href!; } }
            : undefined,
        });
      }
      return;
    }

    const incoming = sortProposalsFreeFirst(result.proposals ?? []);
    if (incoming.length > 0) {
      recordMissionDossier({
        actor: 'ai',
        kind: 'discovery',
        summary: `Integration discovery found ${incoming.length} proposal(s)`,
        reasoning: `Search provider: ${result.searchProvider ?? 'none'}`,
        action: incoming.map((p) => `${p.name} (${p.pricingTier ?? 'unknown'})`).join('; '),
        payload: {
          searchProvider: result.searchProvider,
          proposalIds: incoming.map((p) => p.id),
        },
      });
    } else if (result.searchProvider === 'none') {
      recordMissionDossier({
        actor: 'system',
        kind: 'discovery',
        summary: 'Integration discovery skipped — no web search key',
        action: result.message,
      });
    }
    const existing = loadProposals(missionSessionId).filter((p) => p.status !== 'proposed');
    const merged = [
      ...existing,
      ...incoming.filter((p) => !existing.some((e) => e.connector.id === p.connector.id)),
    ];
    persistProposals(missionSessionId, merged);
    retainMissionGeneratedAsync(missionSessionId, 'integration_proposals', {
      proposals: merged,
      searchProvider: result.searchProvider ?? null,
      discoveredCount: incoming.length,
    });
    set({
      proposals: merged,
      isDiscovering: false,
      lastSearchProvider: result.searchProvider ?? null,
      error: result.message ?? null,
    });

    if (incoming.length > 0) {
      toast.message(
        `Found ${incoming.length} integration candidate${incoming.length === 1 ? '' : 's'} — save to workspace library to reuse`
      );
    } else if (result.searchProvider === 'none') {
      toast.message('No web search key on API — add TAVILY_API_KEY or SERPER_API_KEY for live discovery');
    }
  },

  approveProposal: (id) => {
    // Human gate: connectors never auto-register; mission auto_connect opt-in is separate (missionAutomationPolicy).
    const { missionSessionId, proposals } = get();
    const proposal = proposals.find((p) => p.id === id);
    if (!proposal || proposal.status === 'approved') return;

    const connector: AutoBlocksConnector = {
      ...proposal.connector,
      discoveryMeta: {
        ...proposal.connector.discoveryMeta,
        missionSessionId,
        approvedAt: new Date().toISOString(),
        safetyNotes: proposal.safetyNotes,
        pricingTier: proposal.pricingTier ?? proposal.connector.discoveryMeta?.pricingTier,
      },
    };

    useConnectorLibraryStore.getState().register(connector);

    const next = proposals.map((p) =>
      p.id === id ? { ...p, status: 'approved' as const } : p
    );
    persistProposals(missionSessionId, next);
    retainMissionGeneratedAsync(missionSessionId, 'integration_proposals', { proposals: next });
    set({ proposals: next });
    toast.success(`${connector.name} saved to workspace library — reusable on future Missions`);
  },

  dismissProposal: (id) => {
    const { missionSessionId, proposals } = get();
    const next = proposals.map((p) =>
      p.id === id ? { ...p, status: 'dismissed' as const } : p
    );
    persistProposals(missionSessionId, next);
    retainMissionGeneratedAsync(missionSessionId, 'integration_proposals', { proposals: next });
    set({ proposals: next });
  },

  ingestDirectorProposals: (missionSessionId, incoming) => {
    if (!missionSessionId || incoming.length === 0) return;
    const sorted = sortProposalsFreeFirst(incoming);
    const existing = loadProposals(missionSessionId).filter((p) => p.status !== 'proposed');
    const merged = [
      ...existing,
      ...sorted.filter((p) => !existing.some((e) => e.connector.id === p.connector.id)),
    ];
    persistProposals(missionSessionId, merged);
    retainMissionGeneratedAsync(missionSessionId, 'integration_proposals', {
      proposals: merged,
      source: 'mission_director',
    });
    set({
      missionSessionId,
      proposals: merged,
      lastSearchProvider: null,
      isDiscovering: false,
    });
  },
}));
